import type { FieldType } from 'src/entity/ProjectSettingsForm/types';
import { FormBusinessRule } from 'src/shared/store/projects/types';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const compactRuleConfig = (
  config?: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  if (!isPlainObject(config)) {
    return undefined;
  }

  const result = Object.entries(config).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value === null || value === undefined) {
      return acc;
    }
    if (typeof value === 'string' && !value.trim()) {
      return acc;
    }
    if (Array.isArray(value) && !value.length) {
      return acc;
    }
    if (isPlainObject(value) && !Object.keys(value).length) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});

  if (!Object.keys(result).length) {
    return undefined;
  }

  return result;
};

const getMapSize = (value: unknown): number => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return 0;
  }
  return Object.keys(value).length;
};

const normalizeBusinessRuleDisplayKey = (value: string | undefined): string => {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/[\u00a0\u202f]+/g, ' ')
    .replace(/[^\p{L}\p{N}]+/gu, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
};

export type BusinessRuleTargetColumnIndex = {
  lookup: Record<string, string>;
  ambiguous: Record<string, string[]>;
};

type NormalizeBusinessRulesOptions = {
  targetColumnLookup?: Record<string, string>;
};

type ValidateBusinessRulesOptions = {
  targetColumnIndex?: BusinessRuleTargetColumnIndex;
};

const normalizeTargetColumn = (
  targetColumn: string | undefined,
  targetColumnLookup: Record<string, string>,
): string => {
  const trimmedTargetColumn = (targetColumn || '').trim();
  if (!trimmedTargetColumn) {
    return '';
  }

  return (
    targetColumnLookup[normalizeBusinessRuleDisplayKey(trimmedTargetColumn)] || trimmedTargetColumn
  );
};

export const createBusinessRuleTargetColumnIndex = (
  fields: Pick<FieldType, 'columnLabel' | 'columnName'>[],
): BusinessRuleTargetColumnIndex => {
  const targetsByKey = fields.reduce<Record<string, string[]>>((acc, field) => {
    const columnName = (field.columnName || '').trim().toLowerCase();
    if (!columnName) {
      return acc;
    }

    const candidates = [
      normalizeBusinessRuleDisplayKey(field.columnName),
      normalizeBusinessRuleDisplayKey(field.columnLabel),
    ].filter(Boolean);

    candidates.forEach(key => {
      const nextTargets = new Set(acc[key] || []);
      nextTargets.add(columnName);
      acc[key] = Array.from(nextTargets);
    });

    return acc;
  }, {});

  return Object.entries(targetsByKey).reduce<BusinessRuleTargetColumnIndex>(
    (acc, [key, columnNames]) => {
      if (columnNames.length === 1) {
        acc.lookup[key] = columnNames[0];
        return acc;
      }

      acc.ambiguous[key] = [...columnNames].sort();
      return acc;
    },
    { lookup: {}, ambiguous: {} },
  );
};

export const createBusinessRuleTargetColumnLookup = (
  fields: Pick<FieldType, 'columnLabel' | 'columnName'>[],
): Record<string, string> => {
  return createBusinessRuleTargetColumnIndex(fields).lookup;
};

export const normalizeBusinessRules = (
  rules: FormBusinessRule[],
  options: NormalizeBusinessRulesOptions = {},
): FormBusinessRule[] => {
  const targetColumnLookup = options.targetColumnLookup || {};

  return rules
    .map((rule, index) => {
      const name = (rule.name || '').trim();
      const type = (rule.type || 'math').trim().toLowerCase();
      const expression = (rule.expression || '').trim();
      const targetColumn = normalizeTargetColumn(rule.targetColumn, targetColumnLookup);
      const compactConfig = compactRuleConfig(rule.config as Record<string, unknown>);

      return {
        ...rule,
        id: rule.id || `rule_${index + 1}`,
        name: name || `rule_${index + 1}`,
        type: type || 'math',
        priority:
          Number.isFinite(rule.priority) && Number(rule.priority) > 0
            ? Math.trunc(Number(rule.priority))
            : index + 1,
        ...(expression ? { expression } : {}),
        ...(targetColumn ? { targetColumn } : {}),
        enabled: rule.enabled !== false,
        ...(compactConfig ? { config: compactConfig } : {}),
      };
    })
    .filter(rule => Boolean(rule.name || rule.expression || rule.targetColumn || rule.config));
};

const validateBusinessRulesWithOptions = (
  rules: FormBusinessRule[],
  options: ValidateBusinessRulesOptions = {},
): string[] => {
  const errorsList: string[] = [];
  const allowedTypes = new Set(['math', 'aggregate', 'transfer', 'consolidation', 'python']);
  const targetColumnIndex = options.targetColumnIndex;

  rules.forEach((rule, index) => {
    const ruleName = (rule.name || `rule_${index + 1}`).trim();
    const ruleType = (rule.type || '').trim().toLowerCase();
    const config = (rule.config || {}) as Record<string, unknown>;
    const targetColumnKey = normalizeBusinessRuleDisplayKey(rule.targetColumn);

    if (!ruleName) {
      errorsList.push(`Правило #${index + 1}: требуется название`);
    }
    if (!ruleType || !allowedTypes.has(ruleType)) {
      errorsList.push(`${ruleName}: неподдерживаемый тип "${rule.type || ''}"`);
      return;
    }

    if (targetColumnKey && targetColumnIndex?.ambiguous[targetColumnKey]) {
      errorsList.push(`${ruleName}: неоднозначная целевая колонка "${rule.targetColumn}"`);
    }

    if (ruleType === 'python') {
      const pythonExpression = String(
        rule.expression || config.script || config.expression || '',
      ).trim();
      if (!rule.targetColumn) {
        errorsList.push(`${ruleName}: требуется целевая колонка`);
      }
      if (!pythonExpression) {
        errorsList.push(`${ruleName}: требуется Python-выражение`);
      }
      return;
    }

    if (!rule.targetColumn && ruleType !== 'transfer' && ruleType !== 'consolidation') {
      errorsList.push(`${ruleName}: требуется целевая колонка`);
    }

    if (ruleType === 'math' && !(rule.expression || '').trim()) {
      errorsList.push(`${ruleName}: требуется выражение`);
    }

    if (ruleType === 'aggregate') {
      const fn = String(config.function || '')
        .trim()
        .toLowerCase();
      const sourceColumn = String(config.sourceColumn || '').trim();
      if (!fn) {
        errorsList.push(`${ruleName}: требуется агрегирующая функция`);
      }
      if (fn !== 'count' && !sourceColumn) {
        errorsList.push(`${ruleName}: для агрегирования требуется исходная колонка`);
      }
    }

    if (ruleType === 'transfer') {
      const targetTable = String(config.targetTable || '').trim();
      const columnMapSize = getMapSize(config.columnMap);
      if (!targetTable) {
        errorsList.push(`${ruleName}: требуется целевая таблица`);
      }
      if (columnMapSize === 0) {
        errorsList.push(`${ruleName}: требуется минимум одно сопоставление колонок`);
      }
    }

    if (ruleType === 'consolidation') {
      const targetTable = String(config.targetTable || '').trim();
      const keyMapSize = getMapSize(config.keyMap);
      const valueMapSize = getMapSize(config.valueMap);
      if (!targetTable) {
        errorsList.push(`${ruleName}: требуется таблица-источник`);
      }
      if (keyMapSize === 0) {
        errorsList.push(`${ruleName}: требуется минимум одно сопоставление ключей`);
      }
      if (valueMapSize === 0) {
        errorsList.push(`${ruleName}: требуется минимум одно сопоставление значений`);
      }
    }
  });

  return errorsList;
};

export const validateBusinessRules = (rules: FormBusinessRule[]): string[] => {
  return validateBusinessRulesWithOptions(rules);
};

export const validateBusinessRulesWithContext = (
  rules: FormBusinessRule[],
  options: ValidateBusinessRulesOptions = {},
): string[] => validateBusinessRulesWithOptions(rules, options);
